
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AIAnalysisResult } from "@/hooks/useAIAnalysis";

interface GlobalAnalysisCardProps {
  globalAnalysis: AIAnalysisResult['globalAnalysis'];
}

const GlobalAnalysisCard = ({ globalAnalysis }: GlobalAnalysisCardProps) => {
  const { t } = useTranslation('ai-analysis');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-golf-600" />
          {t('globalAnalysis.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{t('globalAnalysis.overallScore')}</span>
              <Badge variant="default">{globalAnalysis.overallScore}/100</Badge>
            </div>
            <Progress value={globalAnalysis.overallScore} className="h-2" />
          </div>
        </div>
        
        <div className="prose max-w-none">
          <p className="text-gray-700">{globalAnalysis.summary}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="font-semibold text-green-700 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {t('globalAnalysis.strengths')}
            </h4>
            <ul className="space-y-1">
              {globalAnalysis.strengths.map((strength, index) => (
                <li key={index} className="text-sm text-gray-600 pl-2 border-l-2 border-green-200">
                  {strength}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold text-orange-700 flex items-center gap-2">
              <TrendingDown className="h-4 w-4" />
              {t('globalAnalysis.weaknesses')}
            </h4>
            <ul className="space-y-1">
              {globalAnalysis.weaknesses.map((weakness, index) => (
                <li key={index} className="text-sm text-gray-600 pl-2 border-l-2 border-orange-200">
                  {weakness}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {globalAnalysis.trends && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">{t('globalAnalysis.trends')}</h4>
            <p className="text-blue-700">{globalAnalysis.trends}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GlobalAnalysisCard;
